/**
 * Client-side types for the platform execution-ledger API
 * (`/api/platform/executions/*`). Mirrors `lexserver/src/db/models/
 * execution-ledger.ts` (the centralized log of every mission + harness
 * terminal execution, fed by per-user sidecar forwarders).
 *
 * Canonical contract: `ai-docs/lexiun-execution-ledger-plan.md` §C/§D.
 * Consumer page: `lexportal/src/pages/ExecutionLedger.tsx` (handoff
 * `ai-docs/handoffs/execution-ledger-lexportal-monitoring-handoff-2026-06-22.md`).
 *
 * Field semantics shipped + verified live 2026-06-23 (backend commits
 * a38e0c91 / 1a99760c / 8f4e9cb8).
 */

/** A single per-node summary on a mission record — the leaf's status +
 *  rolled metrics (no live progress; progress lives on the per-user
 *  `MissionRecord` only, not the centralized ledger). */
export interface ExecutionLedgerNode {
  nodeId: string;
  harnessId: string;
  status: string;
  durationMs?: number;
  costUsd?: number;
  tokensIn?: number;
  tokensOut?: number;
  /** Per-node summary — the leaf's run essence (shipped 2026-06-23 with
   *  EM). Absent on pre-`8f4e9cb8` records. */
  summary?: string;
}

/** Mission roll-up of all leaves' billable compute (awaiting-user wait
 *  time deliberately excluded per plan §G). */
export interface ExecutionLedgerTotals {
  costUsd: number;
  tokensIn: number;
  tokensOut: number;
}

/** Terminal error on a failed execution. `error.code === "awaiting_user_timeout"`
 *  on a mission is the human-gate-expiry signal — render as
 *  "timed out waiting for input", not a generic failure (handoff §4). */
export interface ExecutionLedgerError {
  code: string;
  message: string;
}

/** The 6 upload-source enum values shipped by the lexruntime stamper +
 *  the migration backfill. Unknown values render via a fallback chip
 *  (rare; only if a new source is introduced upstream). */
export type UploadSource =
  | "lexide"
  | "whatsapp"
  | "telegram"
  | "agent"
  | "api"
  | "migration";

/**
 * One document in the `executionLedger` collection.
 *
 * - `executionId` is unique (`missionId` for missions, `taskId` for
 *   harnesses, `uploadId` for uploads). Upserts are idempotent under
 *   forward-retry.
 * - `kind` discriminates `"mission"` vs `"harness"` vs `"upload"`.
 * - `status` is terminal-only AND server-normalized: a user-cancelled
 *   run reads `"canceled"` (NOT `"failed"` + `error.code:"user_cancelled"`
 *   like the live per-user record). One less special case for the UI.
 * - `title` is the row HEADLINE (harness: manifest `tool.label`;
 *   mission: goal; upload: `originalFilename`). Falls back to `summary`
 *   when absent on older records.
 * - `summary` is always present (required field, fallback chain ends at
 *   harnessId / mission goal / `${email} uploaded ${filename} via ${source}`)
 *   — the row subtitle / detail-pane essence.
 * - All metric fields (`costUsd`/`tokens*`/`durationMs`) are optional;
 *   render "—" when absent.
 * - For `kind: "upload"`, the 11 attribution fields below are ALL
 *   server-required (canonical at ai-docs/upload-attribution-and-delete-permissions.md §2).
 *   `ingestModality` remains optional — absent when nothing classified a
 *   modality. ⚠ NOT keyed to `upload_source`: a PROCESSED SIBLING carries a
 *   modality alongside WHATEVER channel it inherited — `"lexide"` for a lawyer's
 *   upload since lx-kernel eb4401026, and `"agent"` only on the fallback path
 *   where the source recorded no channel. `inheritAttribution` sets
 *   `ingestModality` from its overrides (lx-kernel upload-attribution.ts:346)
 *   in the same object that defaults `uploadSource` (:339), so the two are
 *   independent either way.
 *   Treat absence as "unclassified", never as "this was an agent write" — and
 *   note `upload_source` answers WHERE IT ARRIVED, never who authored it.
 */
export interface ExecutionLedgerRecord {
  executionId: string;
  kind: "mission" | "harness" | "upload";
  status: string;
  /** Short human label for the row headline. Harness = manifest
   *  `tool.label` (NOT the slug); mission = goal; upload =
   *  `originalFilename`. ≤200 chars. Absent on records pre-`8f4e9cb8`
   *  — fall back to `summary`. */
  title?: string;
  /** Human one-liner — the run's essence. Always present. ≤300 chars. */
  summary: string;
  // scope / multi-tenancy
  orgId: string;
  userId?: string;
  clientId?: string;
  matterId?: string;
  caseId?: string;
  // identity / linkage
  missionId?: string; // mission OR a harness leaf belonging to a mission
  taskId?: string;
  parentTaskId?: string;
  // mission-specific
  goal?: string;
  plannerHarnessId?: string;
  nodes?: ExecutionLedgerNode[];
  totals?: ExecutionLedgerTotals;
  // harness-specific
  harnessId?: string;
  /** The harness's *declared* model (manifest), NOT the post-profile-
   *  resolution model (per plan §J) — on a v3 manifest a PROFILE ALIAS
   *  ("LOW"/"MED"/"MAX"). To show what ACTUALLY answered, read
   *  `resolvedModels` below; this field cannot answer it. */
  model?: string;
  provider?: string;
  /** The concrete `provider/model` refs that ACTUALLY answered, distinct and
   *  in first-observed order — the lossless sibling of the declared `model`
   *  above (plan §J, added 2026-08-26). OMITTED (never `[]`) when nothing was
   *  observed, e.g. a run whose LLM calls all bypass the SDK wrappers. */
  resolvedModels?: string[];
  // upload-specific (kind: "upload") — see ai-docs/upload-attribution-and-delete-permissions.md §2
  /** Stable user id of the writer. For system writers (agent harnesses)
   *  this starts with `system:lexiun-agent:<harnessName>`. For the
   *  one-shot legacy backfill the literal sentinel `legacy-unknown`. */
  uploadedById?: string;
  /** Email at the time of upload (denormed — audit truth, not live). */
  uploadedByEmail?: string;
  /** Caller's role at upload time, FROZEN (LAWYER, ORG_ADMIN, PLATFORM_ADMIN,
   *  `system`, `unknown`). Distinct from the live role used for the
   *  Phase-C delete check. */
  uploadedByRoleSnapshot?: string;
  /** ISO-8601 UTC. Distinct from `createdAt` (ms epoch — the ledger
   *  ingest stamp); the upload event itself happened at `uploadedAt`. */
  uploadedAt?: string;
  uploadSource?: UploadSource | string;
  originalFilename?: string;
  originalSize?: number;
  originalContentType?: string;
  /** Pre-canonicalization SHA-256 (hex). Compare against the live
   *  blob's current sha to detect "re-uploaded since stamp". */
  originalSha256?: string;
  /** Optional — `pdf | audio | image | doc`. Absent when UNCLASSIFIED, which is
  *  not the same as agent-written: a processed sibling can carry both a
  *  modality and `upload_source: "agent"`. Do not infer one from the other. */
  ingestModality?: string;
  /** Workspace-relative path, e.g. `raw-intake/e2e-mqu3qu46.pdf`. */
  blobPath?: string;
  // common metrics — ms epoch for timestamps
  createdAt: number;
  startedAt?: number;
  completedAt?: number;
  durationMs?: number;
  costUsd?: number;
  tokensIn?: number;
  tokensOut?: number;
  error?: ExecutionLedgerError;
  // provenance
  sessionKey?: string;
  trace_id?: string;
  forwardedFromService?: string;
  /** Server stamp; TTL anchor (default 90 days, env
   *  `EXECUTION_LEDGER_TTL_DAYS`). ISO string over the wire. */
  receivedAt?: string;
}

/** Filter accepted by `GET /api/platform/executions` (list + stats). */
export interface ExecutionLedgerFilter {
  orgId?: string;
  userId?: string;
  caseId?: string;
  kind?: "mission" | "harness" | "upload";
  status?: string;
  /** `createdAt` lower bound (ms epoch). */
  from?: number;
  /** `createdAt` upper bound (ms epoch). */
  to?: number;
}

/** Resolve the operator-facing "Uploaded by" display string from the
 *  three attribution id/email/role fields. Handles the three writer
 *  classes per the canonical schema (§2.4 of the handoff):
 *  - real user with email → the email
 *  - `system:lexiun-agent:<harnessName>` → "Lexiun Agent (<harnessName>)"
 *  - `legacy-unknown` → "Unknown (backfilled)"
 *  - missing / very old row → "—" */
export function resolveUploaderDisplay(
  id?: string,
  email?: string,
): { label: string; isSystem: boolean; isUnknown: boolean } {
  if (email && email.length > 0) {
    return { label: email, isSystem: false, isUnknown: false };
  }
  if (id?.startsWith("system:lexiun-agent:")) {
    const harnessName = id.slice("system:lexiun-agent:".length) || "agent";
    return {
      label: `Lexiun Agent (${harnessName})`,
      isSystem: true,
      isUnknown: false,
    };
  }
  if (id === "legacy-unknown") {
    return { label: "Unknown (backfilled)", isSystem: false, isUnknown: true };
  }
  return { label: "—", isSystem: false, isUnknown: true };
}

/** `GET /api/platform/executions` response (cursor-paginated, newest first). */
export interface ExecutionLedgerListResult {
  records: ExecutionLedgerRecord[];
  nextCursor: string | null;
}

/** `GET /api/platform/executions/:executionId` response.
 *  `leaves` is non-empty only for missions (grouped by `missionId`);
 *  always `[]` for a standalone harness. */
export interface ExecutionLedgerDetailResult {
  record: ExecutionLedgerRecord;
  leaves: ExecutionLedgerRecord[];
}

/** One uploader returned by `GET /api/org/:orgId/cases/:caseId/uploaders`.
 *  Backs the "Filter by uploader" dropdown. `userId` is the canonical
 *  attribution id (real user id, `system:lexiun-agent:*`, or
 *  `legacy-unknown`); `email`/`roleSnapshot` are the last-seen values
 *  for that id on the case. */
export interface CaseUploader {
  userId: string;
  email: string;
  roleSnapshot: string;
  uploadCount: number;
  lastUploadAt: string;
}

/** `GET /api/org/:orgId/cases/:caseId/uploaders` response. */
export interface CaseUploadersResult {
  uploaders: CaseUploader[];
}

/** Per-bucket point on the stats time series. */
export interface ExecutionLedgerSeriesPoint {
  ts: number;
  count: number;
  errors: number;
}

/** `GET /api/platform/executions/stats` response. */
export interface ExecutionLedgerStatsResult {
  totals: {
    count: number;
    byStatus: Record<string, number>;
    byKind: Record<string, number>;
    costUsd: number;
    tokensIn: number;
    tokensOut: number;
  };
  series: ExecutionLedgerSeriesPoint[];
  topOrgs: Array<{ orgId: string; count: number; costUsd: number }>;
}
